import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { resetCsrfToken } from "@/lib/httpClient";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const AUTH_FLAG = "tuto_authed";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Start loading=false if no flag → instant redirect; loading=true only if flag present
  const [loading, setLoading] = useState(() => !!localStorage.getItem(AUTH_FLAG));
  const hasCheckedRef = useRef(false);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    finally {
      // Logout can be followed by another account without a page reload.
      resetCsrfToken();
      localStorage.removeItem(AUTH_FLAG);
      setUser(null);
    }
  }, []);

  // Global 401 interceptor — any API call that returns 401 clears the session
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || "";
          if (!url.includes("/auth/me") && !url.includes("/auth/login") && !url.includes("/auth/register")) {
            localStorage.removeItem(AUTH_FLAG);
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      localStorage.setItem(AUTH_FLAG, "1");
      setUser(res.data);
    } catch {
      localStorage.removeItem(AUTH_FLAG);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      // Only hit the server if there's a flag suggesting we might be logged in
      if (localStorage.getItem(AUTH_FLAG)) {
        checkAuth();
      } else {
        setLoading(false);
      }
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    resetCsrfToken("user");
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    resetCsrfToken("user");
    localStorage.setItem(AUTH_FLAG, "1");
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, invite_code = null) => {
    resetCsrfToken("user");
    const res = await axios.post(`${API}/auth/register`, { name, email, password, invite_code }, { withCredentials: true });
    resetCsrfToken("user");
    localStorage.setItem(AUTH_FLAG, "1");
    setUser(res.data.user);
    return res.data;
  };

  // Cadastro novo passa pelo pagamento: a conta só existe depois dele.
  const cadastrarComPagamento = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/cadastro/checkout`, { name, email, password },
      { withCredentials: true });
    return res.data;
  };

  const confirmarCadastro = async (sessionId) => {
    resetCsrfToken("user");
    const res = await axios.post(`${API}/auth/cadastro/confirmar`, null,
      { params: { session_id: sessionId }, withCredentials: true });
    resetCsrfToken("user");
    localStorage.setItem(AUTH_FLAG, "1");
    setUser(res.data.user);
    return res.data;
  };

  const setAuthUser = (userData) => {
    resetCsrfToken();
    if (userData) localStorage.setItem(AUTH_FLAG, "1");
    else localStorage.removeItem(AUTH_FLAG);
    setUser(userData);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, cadastrarComPagamento, confirmarCadastro, logout, checkAuth, setAuthUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export { API };
